/**
 * The two ways to add a photo (PLAN §6 step 3): the camera (`capture="environment"`, the rear
 * camera on a phone) and "From gallery" (the same input without `capture`). Each is a real
 * <label> around its input, which is what iOS Safari reliably opens from a tap.
 */

import { Camera, ImagePlus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
    onFile: (file: File) => void;
    /** Shown beside the camera button, e.g. "Photo required". */
    cameraLabel?: string;
    required?: boolean;
    compact?: boolean;
    disabled?: boolean;
    className?: string;
}

export default function PhotoInputs({ onFile, cameraLabel = 'Camera', required, compact, disabled, className }: Props) {
    const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Cleared so the same photo can be chosen again after a failed upload.
        e.target.value = '';
        if (file) onFile(file);
    };
    return (
        <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}>
            <label
                className={cn(
                    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-ring',
                    compact ? 'h-11 px-3 sm:h-9' : 'h-11 px-4 sm:h-10',
                    required
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
                    disabled && 'pointer-events-none opacity-50',
                )}
            >
                <Camera className="h-4 w-4" aria-hidden />
                {cameraLabel}
                <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={pick} disabled={disabled} />
            </label>
            <label
                className={cn(
                    'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-within:ring-2 focus-within:ring-ring',
                    disabled && 'pointer-events-none opacity-50',
                )}
            >
                <ImagePlus className="h-4 w-4" aria-hidden />
                From gallery
                <input type="file" accept="image/*" className="sr-only" onChange={pick} disabled={disabled} />
            </label>
        </div>
    );
}
